import React from 'react';

import moment from 'moment';
import {browserHistory} from 'react-router';
import {PopoverBody} from 'reactstrap';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {StyledPopoverBottomDIV} from 'app/general/bootstrap/utils';
import {
  AttachOverlay,
  OverlayProps,
  PopoverForm,
  PopoverHeading,
} from 'app/general/overlays';
import {cats, withShortcuts} from 'app/general/shortcuts/withShortcuts';
import StateButton from 'app/general/widgets/StateButton';
import {useTriggerTaskTemplate} from 'app/tasks/api';
import {AssignNewTaskModal} from 'app/tasks/components/AssignTaskModal';
import {repeats} from 'app/tasks/utils';
import {gt} from 'app/utils/i18n';

import type {WithShortcutsProps} from 'app/general/shortcuts/withShortcuts';

interface StartDocumentPopoverProps {
  overlayProps: OverlayProps;
  onCancel: () => unknown;
  onMoreOptionsClick: () => unknown;
  onStartClick: () => unknown;
  description: string;
  onDescriptionChange: (description: string) => unknown;
}

const StartDocumentPopover = ({
  onCancel,
  onDescriptionChange,
  description,
  onStartClick,
  onMoreOptionsClick,
  overlayProps,
}: StartDocumentPopoverProps): JSX.Element => (
  <PopoverForm overlayProps={overlayProps}>
    {({closeOnFinish, closeOnSuccess}) => (
      <form onSubmit={(ev) => ev.preventDefault()}>
        <PopoverHeading>{gt('Start Task')}</PopoverHeading>
        <PopoverBody>
          <p>{gt('Start a task based on this document.')}</p>
          <input
            className="form-control form-control-sm"
            name="description"
            autoFocus={true}
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            type="text"
            placeholder={gt('Describe this task')}
          />
          <div className="text-center">
            <StyledMoreOptionsButton
              type="button"
              className="btn btn-link btn-sm mt-2 p-1"
              onClick={() => closeOnFinish(onMoreOptionsClick())}
            >
              {gt('More options')}
            </StyledMoreOptionsButton>
          </div>
        </PopoverBody>
        <StyledPopoverBottomDIV>
          <button
            className="btn btn-sm btn-link"
            onClick={() => closeOnFinish(onCancel())}
            type="button"
          >
            <i className="fa fa-times" /> {gt('Close')}
          </button>
          <StateButton
            className="btn btn-sm btn-primary"
            type="submit"
            busyText={gt('Starting...')}
            onClick={() => closeOnSuccess(onStartClick())}
          >
            {gt('Start')}
          </StateButton>
        </StyledPopoverBottomDIV>
      </form>
    )}
  </PopoverForm>
);

interface Props extends WithShortcutsProps {
  objectId: number;
  contentType: 'procedure' | 'proceduregroup';
}

export const StartDocumentButton = withShortcuts<Props>({
  key: 't',
  name: () => gt('Start document as task'),
  category: cats.detailPage,
})(function StartDocumentButton({
  objectId,
  contentType,
  enableShortcut,
}: Props): JSX.Element {
  const [showModal, setShowModal] = React.useState(false);
  const toggleModal = () => setShowModal((show) => !show);
  const [description, setDescription] = React.useState('');

  const userURL = useCurrentUserState().data!.url;
  const [triggerTaskTemplate] = useTriggerTaskTemplate();

  React.useEffect(() => {
    enableShortcut('t', () => setShowModal(true));
  }, []);

  function createTask() {
    return triggerTaskTemplate({
      task: {
        due: moment().add(2, 'day').format(),
        rrule: 'freq=daily;count=1',
        content_type: contentType,
        object_id: objectId,
        description,
        user_ids: [userURL],
        permission: 'retrieve',
      },
      skipSuccessNotification: true,
    })
      .unwrap()
      .then((response) => browserHistory.push(response.html_url));
  }

  return (
    <>
      <AttachOverlay
        trigger="click"
        overlay={({overlayProps}) => (
          <StartDocumentPopover
            onMoreOptionsClick={toggleModal}
            onStartClick={createTask}
            onDescriptionChange={setDescription}
            onCancel={() => setDescription('')}
            description={description}
            overlayProps={{...overlayProps, placement: 'bottom'}}
          />
        )}
      >
        {(handlers) => (
          <button
            {...handlers}
            title={gt('Start a new task and begin checking off steps')}
            className="btn btn-sm btn-outline-dark js-start-task"
          >
            <span className="fa fa-play" /> {gt('Start')}
          </button>
        )}
      </AttachOverlay>
      {showModal ? (
        <AssignNewTaskModal
          show={showModal}
          onToggle={toggleModal}
          onAssignSuccess={(response) =>
            browserHistory.push(
              typeof response === 'string'
                ? response
                : !repeats(response.task.rrule) && response.html_url != null
                ? response.html_url
                : response.task.html_url
            )
          }
          skipSuccessNotification={true}
          taskData={{
            content_type: contentType,
            object_id: objectId,
            description,
            due: moment().add(2, 'day').format(),
            rrule: 'freq=daily;count=1',
            user_ids: [userURL],
          }}
        />
      ) : null}
    </>
  );
});

const StyledMoreOptionsButton = styled.button`
  font-size: 90%;
`;
